import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppErrors, NotFoundError } from '@fitness-force/models';;
import type { WaiverMasterResponseList } from '@fitness-force/models';

import { environment } from '@fitness-force/environments';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

/** Item from GET /companies/service_categories and GET /franchise_group/{id}/service_categories */
export interface OwnershipScopedServiceCategoryItem {
  category_name?: string;
  category_behaviour?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ServiceCategoryService {
  private http = inject(HttpClient);
  private router = inject(Router);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };


  getServiceCategories(tenantId: number,allParams:Array<string>=[]) {
    let parsedParams = new HttpParams()
    if(allParams.length > 0){

     allParams.forEach((p: any) => {

       parsedParams = parsedParams.append(p.name, p.value);
     });
    }
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.GET_SERVICE_CATEGORIES
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(
        url,
        {headers: this.httpOptions.headers , params: parsedParams}
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  getSingleServiceCategory(tenantId: number,categoryId:number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.GET_SERVICE_CATEGORY_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{category_id}', categoryId.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateServiceCategory(tenantId: number,ser_cat_id:number,data:any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.UPDATE_SERVICE_CATEGORY
      .replace('{tenant_id}', tenantId.toString())
      .replace('{category_id}', ser_cat_id.toString());
    return this.http
      .put(
        url,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }


createServiceCategory(tenantId: number,data:any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.CREATE_SERVICE_CATEGORY
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(
        url,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }



  markActive(tenantId: number,ser_cat_id:number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.MARK_SERVICE_CATEGORY_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{category_id}', ser_cat_id.toString());
    return this.http
      .post(
        url,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActive(tenantId: number,ser_cat_id:number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.MARK_SERVICE_CATEGORY_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{category_id}', ser_cat_id.toString());
    return this.http
      .post(
        url,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getWaiverMasters(tenantId: number):Observable<WaiverMasterResponseList> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.SERVICE_CATEGORY.GET_WAIVER_MASTERS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(
        url,
        {headers: this.httpOptions.headers}
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getAggregatedServiceCategories() {
    return this.http
      .get(
        `${environment.BASE_URL}/distinct_service_categories`,
        this.httpOptions
      ) 
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  /** Company-level service categories for commission rules (?behaviour=AppointmentPackage | ClassPackage). */
  getCompanyServiceCategories(behaviour?: string | null): Observable<OwnershipScopedServiceCategoryItem[]> {
    let params = new HttpParams();
    if (behaviour) {
      params = params.set('behaviour', behaviour);
    }
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_SERVICE_CATEGORIES;
    return this.http.get<OwnershipScopedServiceCategoryItem[]>(url, { headers: this.httpOptions.headers, params }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  /** Franchise-group service categories for commission rules (?behaviour=AppointmentPackage | ClassPackage). */
  getFranchiseGroupServiceCategories(
    franchiseGroupId: number,
    behaviour?: string | null
  ): Observable<OwnershipScopedServiceCategoryItem[]> {
    let params = new HttpParams();
    if (behaviour) {
      params = params.set('behaviour', behaviour);
    }
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_SERVICE_CATEGORIES.replace(
        '{franchise_group_id}',
        franchiseGroupId.toString()
      );
    return this.http.get<OwnershipScopedServiceCategoryItem[]>(url, { headers: this.httpOptions.headers, params }).pipe(
      map((res) => (Array.isArray(res) ? res : [])),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
